import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Grid, Card, CardContent, Button, Box, Stack, Divider, CircularProgress } from '@mui/material';
import { motion } from 'framer-motion';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LaunchIcon from '@mui/icons-material/Launch';
import { supabase } from '../../lib/supabase';
import EditableImage from '../../components/ui/EditableImage';
import SEO from '../../components/seo/SEO';

const ActivitiesPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dateLocale = i18n.language?.startsWith("fr") ? "fr-FR" : "en-US";

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoading(true);
        const { data, error: fetchError } = await supabase
          .from('activities')
          .select(`
            *,
            translations:activities_translations(*)
          `)
          .eq('is_published', true)
          .order('display_order', { ascending: true })
          .order('date', { ascending: false });

        if (fetchError) throw fetchError;

        const currentLang = i18n.language || 'en';
        const localizedActivities = data.map(activity => {
          const translation = activity.translations?.find(t => t.language_code === currentLang) || 
                              activity.translations?.find(t => t.language_code === 'en') || 
                              activity.translations?.[0] || {};
          
          return {
            ...activity,
            title: translation.title || '',
            description: translation.description || '',
            tagline: translation.tagline || ''
          };
        });

        setActivities(localizedActivities);
      } catch (err) {
        console.error('Error fetching activities:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [i18n.language]);

  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'conference': return '#0FA3A3';
      case 'training': return '#D4AF37';
      case 'meetup': return '#0FA3A3';
      case 'publication': return '#D4AF37';
      case 'podcast': return '#0FA3A3';
      default: return '#9CA3AF';
    }
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#0B0F14' }}>
        <CircularProgress sx={{ color: '#0FA3A3' }} />
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ bgcolor: '#0B0F14', minHeight: '100vh', pb: 20 }}>
      <SEO 
        title={t('activities.seo.title', { defaultValue: 'Activités' })}
        description={t('activities.seo.description', { defaultValue: "Conférences, meetups et événements sur le Data Science et l'IA. Découvrez mes interventions et participations." })}
        keywords="conférences, meetups, data science, IA, événements, speaker"
      />
      {/* Header Section */}
      <Box sx={{ py: 15, bgcolor: '#111827', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography 
              variant="overline" 
              align="center"
              sx={{ color: '#D4AF37', fontWeight: 700, letterSpacing: 3, display: 'block', mb: 2 }}
            >
              {t("activitiesPage.header.overline")}
            </Typography>
            <Typography 
              variant="h1" 
              align="center"
              sx={{ color: 'white', mb: 3, fontFamily: 'Philosopher', fontSize: { xs: '2.5rem', md: '3.5rem' } }}
            >
              {t("activitiesPage.header.titlePrefix")} <span style={{ color: '#0FA3A3' }}>{t("activitiesPage.header.titleHighlight")}</span>
            </Typography>
            <Typography 
              variant="h6" 
              align="center"
              sx={{ color: '#9CA3AF', fontWeight: 300, lineHeight: 1.6, maxWidth: '700px', mx: 'auto' }}
            >
              {t("activitiesPage.header.subtitle")}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 10 }}>
        {error ? (
          <Typography color="error" align="center">{t("activitiesPage.errors.load", { error })}</Typography>
        ) : (
          <Grid container spacing={4}>
            {activities.map((activity, index) => (
              <Grid item xs={12} md={6} key={activity.id}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card sx={{ 
                    bgcolor: '#1F2937', 
                    borderRadius: '24px', 
                    overflow: 'hidden', 
                    border: '1px solid rgba(255,255,255,0.05)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      borderColor: getTypeColor(activity.type),
                      boxShadow: `0 20px 40px rgba(0,0,0,0.4)`
                    }
                  }}>
                    <Box sx={{ height: '220px', position: 'relative', overflow: 'hidden' }}>
                      <EditableImage 
                        src={activity.cover_image || activity.image_url?.[0] || 'https://via.placeholder.com/400x220?text=Activity'}
                        alt={activity.title}
                        tableName="activities"
                        recordId={activity.id}
                        sx={{ width: '100%', height: '100%' }}
                        imgSx={{ filter: 'brightness(0.6)' }}
                      />
                      <Box sx={{ 
                        position: 'absolute', 
                        top: 20, 
                        right: 20, 
                        bgcolor: 'rgba(11, 15, 20, 0.8)', 
                        backdropFilter: 'blur(8px)',
                        border: `1px solid ${getTypeColor(activity.type)}`,
                        borderRadius: '10px',
                        px: 2,
                        py: 0.5,
                        zIndex: 5
                      }}>
                        <Typography variant="caption" sx={{ color: getTypeColor(activity.type), fontWeight: 800, textTransform: 'uppercase' }}>
                          {t(`activities.types.${activity.type?.toLowerCase()}`, activity.type)}
                        </Typography>
                      </Box>
                    </Box>

                    <CardContent sx={{ p: 4, flexGrow: 1 }}>
                      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarTodayIcon sx={{ fontSize: '0.9rem', color: '#9CA3AF' }} />
                          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                            {activity.date ? new Date(activity.date).toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' }) : ''}
                          </Typography>
                        </Box>
                        {activity.location && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationOnIcon sx={{ fontSize: '0.9rem', color: '#9CA3AF' }} />
                            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                              {activity.location}
                            </Typography>
                          </Box>
                        )}
                      </Stack>

                      <Typography variant="h4" sx={{ color: 'white', fontWeight: 800, mb: 2, fontFamily: 'Philosopher' }}>
                        {activity.title}
                      </Typography>
                      
                      <Typography variant="body2" sx={{ color: '#9CA3AF', mb: 3, lineHeight: 1.7 }}>
                        {truncateText(activity.tagline || activity.description)}
                      </Typography>
                    </CardContent>

                    <Box sx={{ p: 4, pt: 0 }}>
                      <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', mb: 3 }} />
                      <Button 
                        variant="text" 
                        endIcon={<LaunchIcon />}
                        onClick={() => navigate(`/activities/${activity.id}`)}
                        sx={{ 
                          color: getTypeColor(activity.type), 
                          p: 0, 
                          fontWeight: 700,
                          '&:hover': { bgcolor: 'transparent', opacity: 0.8 }
                        }}
                      >
                        {t("activitiesPage.card.explore")}
                      </Button>
                    </Box>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Call to Collaboration */}
        <Box sx={{ 
          mt: 15, 
          p: { xs: 6, md: 10 }, 
          bgcolor: 'rgba(15, 163, 163, 0.05)', 
          borderRadius: '40px', 
          textAlign: 'center',
          border: '1px solid rgba(15, 163, 163, 0.1)'
        }}>
          <Typography variant="h3" sx={{ color: 'white', mb: 2, fontFamily: 'Philosopher' }}>
            {t("activitiesPage.cta.titlePrefix")} <span style={{ color: '#D4AF37' }}>{t("activitiesPage.cta.titleHighlight")}</span>
          </Typography>
          <Typography variant="body1" sx={{ color: '#9CA3AF', mb: 6, maxWidth: '600px', mx: 'auto' }}>
            {t("activitiesPage.cta.subtitle")}
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            component="a"
            href="/contact"
            sx={{ 
              bgcolor: '#0FA3A3', 
              color: 'white', 
              px: 6, 
              py: 2, 
              borderRadius: '12px', 
              fontWeight: 800,
              '&:hover': { bgcolor: '#0C8A8A' }
            }}
          >
            {t("activitiesPage.cta.button")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ActivitiesPage;
